'use client';

import { BRAND as SITE_BRAND, withBase } from '../lib/brand';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Tag, Spin, Avatar } from 'antd';
import { useTripChatContext } from '../contexts/TripChatContext';
import { usePathname } from 'next/navigation';
import { isSharePagePath } from '../lib/sharePage';
import { trackChatOpen, trackChatMessage } from '../lib/analytics';
import PreviewAddToCart from './store/PreviewAddToCart';
import { useCaPreview } from '../lib/caPreview';
import {
  SendOutlined,
  MessageOutlined,
  UserOutlined,
  EnvironmentOutlined,
  StarFilled,
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  ShoppingOutlined,
  GoogleOutlined,
  FacebookOutlined,
  LoginOutlined,
  HeartOutlined,
  HeartFilled,
  ClearOutlined,
  CalendarOutlined,
  TeamOutlined,
  CarOutlined,
} from '@ant-design/icons';

const API_BASE = (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

// ── Brand colours — match site's blue palette ────────────────────
const BRAND = '#0f172a';       // slate-900 (header, bubble, user bubbles)
const BRAND_DARK = '#020617';  // slate-950 (hover)
const ACCENT = '#1a68d1';      // brand blue (send button, suggestion chips, prices)

// ── Types ────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  data?: StructuredData;
  allData?: StructuredData[];
  /** Follow-up chips the agent sent (`quick_replies` event). When present
   *  they replace bullet-list extraction, which turned step lists and
   *  "On the web / In the app" lists into bogus chips. */
  quickReplies?: string[];
}

interface StructuredData {
  type: 'search_results' | 'campsite_details' | 'campsite_details_list' | 'availability' | 'product_results' | 'order_tracking' | 'campervan_results' | 'trip_saved' | 'trip_updated';
  payload: any;
}

interface CampsiteDetail {
  id: number;
  name: string;
  description?: string;
  short_description?: string;
  rating?: number;
  image_url?: string;
  booking_url?: string;
  url?: string;
  features?: string[];
  address?: {
    street?: string;
    town?: string;
    state?: string;
    postcode?: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
  };
  is_premium?: boolean;
}

interface CampsiteResult {
  id: number;
  name: string;
  short_description?: string;
  rating?: number;
  image_url?: string;
  town?: string;
  state?: string;
  distance_km?: number;
  features?: string[];
  is_premium?: boolean;
  url?: string;
  booking_url?: string;
}

interface ProductResult {
  title: string;
  handle: string;
  image_url?: string;
  vendor?: string;
  product_type?: string;
  price?: number;
  compare_at_price?: number;
  product_url?: string;
}

interface CampervanOffer {
  brand?: string | null;
  brand_logo?: string | null;
  name?: string | null;
  booking_url?: string | null;
  category?: string | null;
  style?: string | null;
  image?: string | null;
  sleeps?: number | null;
  seats?: number | null;
  four_wd?: boolean;
  price_aud?: number | null;
  total_aud?: number | null;
  currency?: string;
  rental_days?: number | null;
  available?: boolean;
  tags?: string[];
}

interface CampervanResultsPayload {
  offers: CampervanOffer[];
  offer_count?: number;
  cheapest_aud?: number | null;
  pickup_city?: string;
  rental_days?: number | null;
  booking_url?: string;
  query?: {
    pickup_city?: string;
    pickup_date?: string;
    dropoff_date?: string;
    driver_age?: number;
    passengers?: number;
  };
  error?: string;
}

interface OrderTrackingResult {
  found: boolean;
  order_number?: string;
  order_date?: string;
  status?: string;
  items?: Array<{ title: string; quantity: number }>;
  shipping_location?: string;
  tracking?: Array<{
    carrier: string;
    tracking_number: string;
    tracking_url: string;
  }>;
  account_url?: string;
  message?: string;
}

interface SessionContext {
  vehicle_type?: string;
  vehicle_length?: string;
  group_size?: string;
  has_pets?: string;
  page_context?: string;
}

export interface PageContext {
  pageType: 'campsite' | 'search' | 'home' | 'product' | 'general';
  entityId?: number;
  entityName?: string;
  bookingUrl?: string;
  productType?: string;
  productName?: string;
}

// ── Component ────────────────────────────────────────────────────

interface TripChatProps {
  defaultExpanded?: boolean;
  className?: string;
  initialContext?: SessionContext;
  pageContext?: PageContext;
}

function getSuggestions(ctx?: PageContext): string[] {
  if (!ctx) {
    return [
      'Dog-friendly campsite near Sydney',
      'Caravan park with powered sites in QLD',
      'Free camping near the coast',
      'Track my order',
    ];
  }
  switch (ctx.pageType) {
    case 'campsite':
      return [
        ctx.bookingUrl
          ? (ctx.entityName ? `Check availability at ${ctx.entityName}` : 'Check availability here')
          : (ctx.entityName ? `How do I book ${ctx.entityName}?` : 'How do I book this campsite?'),
        'What gear do I need for this campsite?',
        'Find similar campsites nearby',
        'Is this campsite dog-friendly?',
      ];
    case 'product':
      return [
        'Where can I use this gear?',
        'Find similar products',
        ctx.productName ? `Is ${ctx.productName} good for beach camping?` : 'Is this good for beach camping?',
        'What campsites suit this gear?',
      ];
    default:
      return [
        'Dog-friendly campsite near Sydney',
        'Caravan park with powered sites in QLD',
        'Free camping near the coast',
        'Track my order',
      ];
  }
}

// ── Option extraction helper ──────────────────────────────────
// Detects bullet-point option lists in assistant messages and extracts
// them as clickable choices, leaving the rest of the text clean.

interface ExtractedOptions {
  textBefore: string;
  options: string[];
  textAfter: string;
}

function extractOptions(content: string): ExtractedOptions | null {
  // Find the LAST bullet list in the message — options/choices are typically
  // at the end of a message, while earlier bullet lists are descriptions.
  const lines = content.split('\n');

  // Collect all bullet list blocks
  const blocks: Array<{ start: number; end: number; items: string[] }> = [];
  let currentBlock: { start: number; end: number; items: string[] } | null = null;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (/^[-•]\s+/.test(trimmed)) {
      if (!currentBlock) {
        currentBlock = { start: i, end: i, items: [] };
      }
      currentBlock.end = i;
      // Extract option text: strip bullet, bold markers, and trailing description
      let opt = trimmed.replace(/^[-•]\s+/, '');
      const boldMatch = opt.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        opt = boldMatch[1];
      } else {
        const parenIdx = opt.indexOf('(');
        const dashIdx = opt.indexOf(' — ');
        const colonIdx = opt.indexOf(': ');
        const cutoff = Math.min(
          parenIdx > 0 ? parenIdx : 999,
          dashIdx > 0 ? dashIdx : 999,
          colonIdx > 0 ? colonIdx : 999,
        );
        if (cutoff < 999) opt = opt.substring(0, cutoff);
      }
      currentBlock.items.push(opt.trim());
    } else if (currentBlock && trimmed === '') {
      continue; // Allow blank lines within a block
    } else if (currentBlock) {
      if (currentBlock.items.length >= 2) blocks.push(currentBlock);
      currentBlock = null;
    }
  }
  // Don't forget the last block if message ends with bullets
  if (currentBlock && currentBlock.items.length >= 2) blocks.push(currentBlock);

  // Use the LAST block with 2+ items — that's the one with choices
  if (blocks.length === 0) return null;
  const block = blocks[blocks.length - 1];

  const textBefore = lines.slice(0, block.start).join('\n').trim();
  const textAfter = lines.slice(block.end + 1).join('\n').trim();

  return { textBefore, options: block.items, textAfter };
}

// ── Parse markdown-style links and render booking URLs as styled cards ──
function renderMessageContent(content: string): React.ReactNode {
  // Split content on markdown links: [text](url)
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkRegex.exec(content)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(
        <React.Fragment key={`t-${lastIndex}`}>
          {renderBoldText(content.slice(lastIndex, match.index))}
        </React.Fragment>
      );
    }

    const linkText = match[1];
    const url = match[2];
    const isBookingLink = url.includes('bookeasy.com') || url.includes('booking');

    if (isBookingLink) {
      // Render as a styled booking card/button
      parts.push(
        <a
          key={`link-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block my-2 no-underline"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            background: 'linear-gradient(135deg, #1a68d1 0%, #1558b0 100%)',
            color: '#fff',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
            boxShadow: '0 2px 6px rgba(22,100,58,0.3)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(22,100,58,0.4)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(22,100,58,0.3)';
          }}
        >
          <span style={{ fontSize: 18 }}>🔗</span>
          <span>{linkText}</span>
          <span style={{ marginLeft: 'auto', opacity: 0.8, fontSize: 11 }}>↗</span>
        </a>
      );
    } else {
      // Regular link — render as inline styled anchor
      parts.push(
        <a
          key={`link-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1a68d1', textDecoration: 'underline' }}
        >
          {linkText}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(
      <React.Fragment key={`t-${lastIndex}`}>
        {renderBoldText(content.slice(lastIndex))}
      </React.Fragment>
    );
  }

  return parts.length > 0 ? parts : renderBoldText(content);
}

// ── Render **bold** text ──
function renderBoldText(text: string): React.ReactNode {
  const boldRegex = /\*\*(.+?)\*\*/g;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;

  while ((m = boldRegex.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(text.slice(lastIdx, m.index));
    }
    parts.push(<strong key={`b-${m.index}`}>{m[1]}</strong>);
    lastIdx = m.index + m[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx));
  }

  return parts.length > 0 ? parts : text;
}

export default function TripChat({
  defaultExpanded = false,
  className = '',
  initialContext,
  pageContext: pageContextProp,
}: TripChatProps) {
  const { pageContext: pageContextFromProvider } = useTripChatContext();
  const pageContext = pageContextProp ?? pageContextFromProvider;
  // A shared trip page has its own action row and map toggle; the bubble
  // covered "Get the app" and the map's zoom controls.
  const onSharePage = isSharePagePath(usePathname());
  // ca_preview: the closed launcher wears the store's chat pill.
  const caPreview = useCaPreview();

  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const wasClosed = localStorage.getItem('tripChatClosed');
      if (wasClosed === 'true') return false;
    }
    return defaultExpanded;
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Device location (for "near me" agent queries). Requested once when the
  // chat opens; attached to messages as context_updates when available.
  const userLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const geoRequestedRef = useRef(false);

  // Detect whether the MobileCTABar is present (sets data-mobile-cta on <html>)
  // and whether the trip planner owns the screen (data-trip-planner = 'rail'
  // on desktop, 'sheet' on phones; set by the explorer). The planner modes
  // shrink the bubble to an icon and move it off the planner's content.
  const [hasMobileCTA, setHasMobileCTA] = useState(false);
  const [plannerMode, setPlannerMode] = useState<'' | 'rail' | 'sheet'>('');
  // data-trip-chat-docked: the trip planner's toolbar carries its own "Ask AI"
  // button, so the floating bubble steps aside instead of sitting on the map.
  const [docked, setDocked] = useState(false);
  // data-chat-fab: the page (the explorer) wants the store chat control as a
  // floating button on phones, where its results-bar link doesn't fit.
  const [fabWanted, setFabWanted] = useState(false);
  useEffect(() => {
    const check = () => {
      setHasMobileCTA(document.documentElement.hasAttribute('data-mobile-cta'));
      const m = document.documentElement.getAttribute('data-trip-planner');
      // data-planner-sheet: the phone planner's bottom sheet is up (set by
      // PlannerBottomSheet itself). Its ⋯ menu carries "Ask Trippy".
      const sheetUp = document.documentElement.hasAttribute('data-planner-sheet');
      setPlannerMode(sheetUp ? 'sheet' : m === 'rail' || m === 'sheet' ? m : '');
      setDocked(document.documentElement.hasAttribute('data-trip-chat-docked'));
      setFabWanted(document.documentElement.hasAttribute('data-chat-fab'));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-mobile-cta', 'data-trip-planner', 'data-trip-chat-docked', 'data-planner-sheet', 'data-chat-fab'],
    });
    return () => observer.disconnect();
  }, []);
  // Anything on the page can open the assistant: dispatch 'trip-chat-open'.
  useEffect(() => {
    const open = () => {
      try {
        localStorage.setItem('tripChatClosed', 'false');
      } catch {
        /* private mode */
      }
      setIsOpen(true);
      trackChatOpen();
    };
    window.addEventListener('trip-chat-open', open);
    return () => window.removeEventListener('trip-chat-open', open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    // Restore session ID from localStorage on mount
    if (typeof window !== 'undefined') {
      return localStorage.getItem('campingAssistantSessionId');
    }
    return null;
  });
  const [statusText, setStatusText] = useState('');
  const [loginRequired, setLoginRequired] = useState(false);
  // What the sign-in wall is for: saving a trip the assistant planned, or the
  // older availability/booking gate.
  const [loginAction, setLoginAction] = useState<'save_trip' | 'other'>('other');
  const [sessionRestored, setSessionRestored] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Clear chat and start a new session
  const clearChat = useCallback(() => {
    setMessages([]);
    setInput('');
    setStatusText('');
    setSessionId(null);
    setSessionRestored(false);
    setLoginRequired(false);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('campingAssistantSessionId');
    }
    // Refresh suggestions for new session
    const ctx = pageContext
      ? JSON.stringify({ pageType: pageContext.pageType, entityName: pageContext.entityName })
      : undefined;
    const url = `${API_BASE}/agents/suggestions?refresh=1${ctx ? `&context=${encodeURIComponent(ctx)}` : ''}`;
    fetch(url, { headers: { 'X-App-Source': 'camping-australia-web' } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.suggestions?.length) setSuggestions(data.suggestions); })
      .catch(() => {});
    // Focus the input after clearing
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [pageContext]);

  // Persist sessionId to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionId) {
      localStorage.setItem('campingAssistantSessionId', sessionId);
    }
  }, [sessionId]);

  // Restore conversation history from backend when we have a sessionId
  // but no messages (i.e. after a page reload / OAuth redirect)
  useEffect(() => {
    if (!sessionId || messages.length > 0 || sessionRestored) return;
    setSessionRestored(true);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/agents/session/${sessionId}`);
        if (!res.ok) {
          // Session expired or doesn't exist — clear it
          localStorage.removeItem('campingAssistantSessionId');
          setSessionId(null);
          return;
        }
        const data = await res.json();
        if (data.history && data.history.length > 0) {
          // Rebuild structured card data from message metadata so cards
          // (campsites, campervans, orders) survive a page reload — the
          // restore path used to keep only text, which silently deleted
          // every Book button the user had been shown.
          const metadataToData = (md: Record<string, any> | undefined): StructuredData[] => {
            if (!md) return [];
            const types: StructuredData['type'][] = [
              'search_results', 'campsite_details_list', 'availability',
              'product_results', 'order_tracking', 'campervan_results',
            ];
            const out: StructuredData[] = [];
            for (const t of types) {
              if (!md[t]) continue;
              try {
                out.push({ type: t, payload: typeof md[t] === 'string' ? JSON.parse(md[t]) : md[t] });
              } catch {
                // unparseable payload — skip, text still restores
              }
            }
            return out;
          };
          const restored: ChatMessage[] = data.history.map(
            (msg: { role: string; content: string; metadata?: Record<string, any> }, i: number) => {
              const allData = msg.role === 'assistant' ? metadataToData(msg.metadata) : [];
              return {
                id: `restored-${i}`,
                role: msg.role as 'user' | 'assistant',
                content: msg.content,
                timestamp: new Date(),
                data: allData.length > 0 ? allData[0] : undefined,
                allData: allData.length > 0 ? allData : undefined,
              };
            }
          );
          setMessages(restored);
          // Auto-open chat if we have history, unless user explicitly closed it
          if (localStorage.getItem('tripChatClosed') !== 'true') {
            setIsOpen(true);
          }
        }
      } catch {
        // Silently fail — fresh session will be created on next message
      }
    })();
  }, [sessionId, messages.length, sessionRestored]);

  // Opened from elsewhere on the page (the trip planner docks its own
  // "AI Camping Assistant" button in the results bar).
  useEffect(() => {
    const open = () => {
      localStorage.setItem('tripChatClosed', 'false');
      setIsOpen(true);
      trackChatOpen();
    };
    window.addEventListener('tp:open-chat', open);
    return () => window.removeEventListener('tp:open-chat', open);
  }, []);

  // Escape closes the open panel, like the store's other dialogs. Expanded
  // mode steps back to the docked panel first.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (isExpanded) {
        setIsExpanded(false);
        return;
      }
      try {
        localStorage.setItem('tripChatClosed', 'true');
      } catch {
        /* private mode */
      }
      setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isExpanded]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, statusText]);

  // Focus input when chat opens; also request device location once so the
  // agent can answer "near me" queries without asking for coordinates.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (!geoRequestedRef.current && typeof navigator !== 'undefined' && navigator.geolocation) {
        geoRequestedRef.current = true;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            userLocationRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          },
          () => { /* denied/unavailable — the agent will ask for a location */ },
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
        );
      }
    }
  }, [isOpen]);

  // ── LLM-generated suggestion chips ───────────────────────────

  useEffect(() => {
    const ctx = pageContext
      ? JSON.stringify({ pageType: pageContext.pageType, entityName: pageContext.entityName, productName: pageContext.productName })
      : undefined;
    const url = `${API_BASE}/agents/suggestions${ctx ? `?context=${encodeURIComponent(ctx)}` : ''}`;
    fetch(url, { headers: { 'X-App-Source': 'camping-australia-web' } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.suggestions?.length) setSuggestions(data.suggestions); })
      .catch(() => {});
  }, [pageContext?.pageType, pageContext?.entityName]);

  // ── Auth helpers ──────────────────────────────────────────────

  function getReviewerToken(): string | null {
    if (typeof window === 'undefined') return null;
    // Support both userToken (new) and reviewerToken (legacy migration)
    return localStorage.getItem('userToken') || localStorage.getItem('reviewerToken');
  }

  function isLoggedIn(): boolean {
    return !!getReviewerToken();
  }

  // The trip the user is actively editing (if the planner is open on this
  // page). Best-effort hint so the chat targets THAT trip; the backend
  // validates ownership and falls back to the user's most-recent trip.
  function getActiveTripId(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem('trip-planner.activeTrip');
      if (!raw) return null;
      const t = JSON.parse(raw);
      return t && typeof t.id === 'string' ? t.id : null;
    } catch {
      return null;
    }
  }

  function handleLogin(provider: 'google' | 'facebook') {
    const redirectUrl = typeof window !== 'undefined' ? window.location.href : '';
    window.location.href = `${API_BASE}/reviewer/auth/${provider}/login?redirect_to=${encodeURIComponent(redirectUrl)}`;
  }

  // ── Session Management ──────────────────────────────────────

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    // Check localStorage in case state hasn't synced yet
    const stored = typeof window !== 'undefined'
      ? localStorage.getItem('campingAssistantSessionId')
      : null;
    if (stored) {
      setSessionId(stored);
      return stored;
    }

    const ctx: SessionContext = { ...initialContext };
    if (pageContext) {
      ctx.page_context = JSON.stringify(pageContext);
    }

    try {
      const res = await fetch(`${API_BASE}/agents/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: ctx }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      return data.session_id;
    } catch (err) {
      console.error('Failed to create session:', err);
      throw err;
    }
  }, [sessionId, initialContext, pageContext]);

  // ── Send Message ────────────────────────────────────────────

  const sendMessage = useCallback(async (overrideText?: string) => {
    const text = (overrideText || input).trim();
    if (!text || isLoading) return;

    setInput('');
    setIsLoading(true);
    setStatusText('Thinking...');
    setLoginRequired(false);
    trackChatMessage('user');

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const sid = await ensureSession();

      // "near me" sent before the open-time location request resolved: ask
      // once more and wait briefly, so the agent gets real coordinates
      // instead of guessing a city.
      if (!userLocationRef.current && /\b(near me|nearby|around here|closest|close to me)\b/i.test(text)
          && typeof navigator !== 'undefined' && navigator.geolocation) {
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              userLocationRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
              done();
            },
            done,
            { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 },
          );
          setTimeout(done, 5500);
        });
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = getReviewerToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Context the agent needs: device GPS (for "near me") + the active trip
      // id (so "add this to my trip" edits the right trip). auth_token is taken
      // from the Authorization header server-side, so it's not sent here.
      // platform: the assistant answers "where is my trip?" differently on the
      // web than in the apps.
      const contextUpdates: Record<string, string> = { platform: 'web' };
      if (userLocationRef.current) {
        contextUpdates.user_lat = String(userLocationRef.current.lat);
        contextUpdates.user_lng = String(userLocationRef.current.lng);
      }
      const activeTripId = getActiveTripId();
      if (activeTripId) contextUpdates.active_trip_id = activeTripId;

      const res = await fetch(`${API_BASE}/agents/session/${sid}/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: text,
          task_type: 'conversation',
          ...(Object.keys(contextUpdates).length ? { context_updates: contextUpdates } : {}),
        }),
      });

      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';
      let quickReplies: string[] = [];
      const allStructuredData: StructuredData[] = [];

      if (reader) {
        // eventType MUST survive chunk boundaries: a large payload (e.g.
        // campervan_results at ~30KB) puts the `event:` line and its `data:`
        // line in different network reads. Declaring this inside the loop
        // reset it between reads and silently dropped those events.
        let eventType = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ') && eventType) {
              try {
                const eventData = JSON.parse(line.slice(6));
                switch (eventType) {
                  case 'status':
                    setStatusText(eventData.message || 'Processing...');
                    break;
                  case 'text':
                    assistantContent = eventData.content || '';
                    break;
                  case 'data':
                    if (eventData.type === 'trip_updated') {
                      // The agent edited the user's saved trip — tell the rest
                      // of the app (planner ladder/map) to refresh in place.
                      if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                          new CustomEvent('trip-updated', { detail: eventData.payload })
                        );
                      }
                    }
                    allStructuredData.push({
                      type: eventData.type,
                      payload: eventData.payload,
                    });
                    break;
                  case 'quick_replies':
                    quickReplies = Array.isArray(eventData.options)
                      ? eventData.options.filter((o: unknown) => typeof o === 'string')
                      : [];
                    break;
                  case 'done':
                    setStatusText('');
                    break;
                  case 'error':
                    assistantContent = eventData.message || 'Sorry, I\u2019m having trouble right now. Please try again in a moment.';
                    break;
                  case 'login_required':
                    // Save the assistant's text message (and any cards) before
                    // showing the login wall.
                    if (assistantContent) {
                      const cards = allStructuredData.slice();
                      // Copy now: the updater runs after assistantContent is
                      // reset below, which rendered an empty bubble.
                      const text = assistantContent;
                      const chips = quickReplies.slice();
                      setMessages(prev => [...prev, {
                        id: `assistant-${Date.now()}`,
                        role: 'assistant',
                        content: text,
                        timestamp: new Date(),
                        data: cards.length > 0 ? cards[0] : undefined,
                        allData: cards.length > 0 ? cards : undefined,
                        quickReplies: chips.length > 0 ? chips : undefined,
                      }]);
                      assistantContent = '';
                    }
                    setLoginAction(eventData.gated_action === 'save_trip' ? 'save_trip' : 'other');
                    setLoginRequired(true);
                    setIsLoading(false);
                    setStatusText('');
                    return;
                }
              } catch {
                // skip malformed JSON
              }
              eventType = '';
            }
          }
        }
      }

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: assistantContent || 'I couldn\'t process that request. Please try again.',
        timestamp: new Date(),
        data: allStructuredData.length > 0 ? allStructuredData[0] : undefined,
        allData: allStructuredData.length > 0 ? allStructuredData : undefined,
        quickReplies: quickReplies.length > 0 ? quickReplies : undefined,
      };
      setMessages(prev => [...prev, assistantMsg]);

    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Sorry, I\'m having trouble connecting. Please try again in a moment.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setStatusText('');
    }
  }, [input, isLoading, ensureSession]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Chat Bubble (collapsed state) ──────────────────────────

  // On mobile, shift up above the MobileCTABar when it's present (bar ≈ 80px).
  // globals.css does the same from first paint with :has(.mobile-cta-bar);
  // this class is the fallback for browsers without :has. Before that rule the
  // bubble jumped 72px after hydration on every campsite page.
  const bubbleBottom = hasMobileCTA ? 'bottom-[96px] md:bottom-6' : 'bottom-6';

  // Phones with the planner sheet up: no floating bubble over the map's
  // labels. The planner's ⋯ menu carries "Ask Trippy about this trip".
  if (!isOpen && (docked || plannerMode === 'sheet' || onSharePage)) return null;

  if (!isOpen) {
    // Phones always get a 56px icon button (a labelled pill sat on top of
    // headlines). Desktop keeps the label, except beside the trip planner,
    // where it becomes a 44px icon stacked above the map's Layers and zoom
    // controls (one column, one rhythm). On phones with the planner sheet up, it rides
    // just above the sheet (--tp-fab-bottom, set by the explorer).
    const placement =
      plannerMode === 'rail'
        ? 'bottom-[163px] right-[calc(440px+16px)]'
        : plannerMode === 'sheet'
          ? 'right-3'
          : `${bubbleBottom} right-4 md:right-6`;
    const iconOnlyDesktop = plannerMode === 'rail';
    // ca_preview: store pages carry no floating overlay, so outside the
    // planner there is no launcher over the content (the page offers an
    // "Ask our camping assistant" link instead, which fires trip-chat-open).
    // Exception: the explorer on phones (data-chat-fab), where the results
    // bar's link has no room — the same store control, phones only.
    const phoneFab = caPreview && plannerMode !== 'sheet' && fabWanted;
    if (caPreview && plannerMode !== 'sheet' && !phoneFab) return null;
    if (caPreview) {
      // The phone planner sheet: the map's control, riding just above the sheet.
      const pillPlacement = plannerMode === 'sheet' ? 'right-3' : `${bubbleBottom} right-4 md:right-6`;
      return (
        <div
          className={`trip-chat-float fixed ${pillPlacement} z-50 ${phoneFab ? 'md:hidden' : ''} ${className}`}
          style={plannerMode === 'sheet' ? { bottom: 'var(--tp-fab-bottom, 24px)' } : undefined}
        >
          <button
            type="button"
            onClick={() => { localStorage.setItem('tripChatClosed', 'false'); setIsOpen(true); trackChatOpen(); }}
            aria-label="Chat with the camping assistant"
            className="ca-chat-ctl"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5.5h16v10H9l-5 4v-14z"/></svg>
          </button>
        </div>
      );
    }
    return (
      <div
        className={`trip-chat-float fixed ${placement} z-50 ${className}`}
        style={plannerMode === 'sheet' ? { bottom: 'var(--tp-fab-bottom, 24px)' } : undefined}
      >
        <button
          onClick={() => { localStorage.setItem('tripChatClosed', 'false'); setIsOpen(true); trackChatOpen(); }}
          aria-label="AI Camping Assistant"
          title="AI Camping Assistant"
          className={`flex ${plannerMode === 'rail' ? 'h-10 w-10 min-w-0' : plannerMode ? 'h-11 w-11' : 'h-14 w-14'} min-h-0 items-center justify-center rounded-full text-white shadow-lg transition-[transform,background-color] duration-150 hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 ${
            iconOnlyDesktop ? '' : 'md:h-auto md:w-auto md:gap-2 md:px-5 md:py-3'
          }`}
          style={{ background: BRAND }}
          onMouseEnter={e => (e.currentTarget.style.background = BRAND_DARK)}
          onMouseLeave={e => (e.currentTarget.style.background = BRAND)}
        >
          <MessageOutlined className={plannerMode === 'rail' ? 'text-[17px]' : 'text-[22px] md:text-lg'} />
          {!iconOnlyDesktop && <span className="hidden text-sm font-semibold md:inline">AI Camping Assistant</span>}
        </button>
      </div>
    );
  }

  // ── Chat Panel (open state) ────────────────────────────────

  const panelClasses = isExpanded
    ? 'fixed inset-4 z-50'
    : `trip-chat-float fixed ${bubbleBottom} right-4 md:right-6 z-50 w-[420px] h-[600px] max-h-[80vh]`;

  return (
    <div className={`${panelClasses} ${className}`}>
      <div className="flex flex-col h-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 text-white"
          style={{ background: BRAND }}
        >
          <div className="flex items-center gap-2">
            <Avatar
              size={32}
              icon={<MessageOutlined />}
              className="bg-white/20"
            />
            <div>
              <div className="font-semibold text-sm leading-tight">AI Camping Assistant</div>
              <div className="text-[11px] text-white/70">Ask me anything about camping</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="New conversation"
              >
                <ClearOutlined />
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
            </button>
            <button
              onClick={() => { localStorage.setItem('tripChatClosed', 'true'); setIsOpen(false); }}
              className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 bg-gray-50">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <MessageOutlined className="text-4xl mb-3 text-slate-300" />
              <p className="text-gray-500 text-sm mb-4">
                G&apos;day! I&apos;m your AI Camping Assistant. I can help you find campsites, check weather forecasts, explore fishing spots, plan road trips, or answer any questions about camping in Australia.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion);
                      sendMessage(suggestion);
                    }}
                    className="text-xs px-3 py-1.5 rounded-full transition-colors"
                    style={{
                      background: `${ACCENT}18`,
                      color: ACCENT,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = `${ACCENT}30`)}
                    onMouseLeave={e => (e.currentTarget.style.background = `${ACCENT}18`)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, msgIdx) => {
            // Extract clickable options from assistant messages
            // The agent's own quick replies win; bullet extraction is the
            // fallback for messages that came without them.
            const extracted = msg.role === 'assistant' && !isLoading
              ? (msg.quickReplies?.length
                  ? { textBefore: msg.content, options: msg.quickReplies, textAfter: '' }
                  : extractOptions(msg.content))
              : null;
            // Only show option buttons on the LAST assistant message
            const isLastAssistant = msg.role === 'assistant'
              && msgIdx === messages.map(m => m.role).lastIndexOf('assistant');
            const showOptions = extracted && isLastAssistant;

            return (
              <div key={msg.id}>
                {/* Message bubble */}
                <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <Avatar size={28} icon={<MessageOutlined />} style={{ background: BRAND }} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'text-white rounded-br-md'
                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'
                    }`}
                    style={msg.role === 'user' ? { background: BRAND } : undefined}
                  >
                    <div className="whitespace-pre-wrap">
                      {showOptions ? (
                        <>
                          {renderMessageContent(extracted.textBefore)}
                          {extracted.textAfter && <>{'\n\n'}{renderMessageContent(extracted.textAfter)}</>}
                        </>
                      ) : (
                        msg.role === 'assistant' ? renderMessageContent(msg.content) : msg.content
                      )}
                    </div>
                  </div>
                  {msg.role === 'user' && (
                    <Avatar size={28} icon={<UserOutlined />} className="bg-gray-400 flex-shrink-0 mt-0.5" />
                  )}
                </div>

                {/* Clickable option buttons */}
                {showOptions && (
                  <div className="mt-2 ml-9 flex flex-wrap gap-2">
                    {extracted.options.map(opt => (
                      <button
                        key={opt}
                        onClick={() => sendMessage(opt)}
                        disabled={isLoading}
                        className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                        style={{
                          borderColor: ACCENT,
                          color: ACCENT,
                          background: `${ACCENT}10`,
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = ACCENT;
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = `${ACCENT}10`;
                          e.currentTarget.style.color = ACCENT;
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                    <button
                      onClick={() => inputRef.current?.focus()}
                      className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
                    >
                      Other...
                    </button>
                  </div>
                )}

                {/* Render ALL structured data attached to this message */}
                {(msg.allData || (msg.data ? [msg.data] : [])).map((sd, sdIdx) => {
                  if (sd.type === 'search_results' && sd.payload?.campsites) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 space-y-2 ml-9">
                        {sd.payload.campsites.slice(0, 6).map((site: CampsiteResult) => (
                          <CampsiteCard key={site.id} site={site} />
                        ))}
                      </div>
                    );
                  }
                  if (sd.type === 'campsite_details_list' && Array.isArray(sd.payload)) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 space-y-2 ml-9">
                        {(sd.payload as CampsiteDetail[]).map((detail) => (
                          <CampsiteDetailCard key={detail.id} detail={detail} />
                        ))}
                      </div>
                    );
                  }
                  if (sd.type === 'product_results' && sd.payload?.products) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 space-y-2 ml-9">
                        {sd.payload.products.slice(0, 6).map((product: ProductResult) => (
                          <ProductCard key={product.handle} product={product} />
                        ))}
                      </div>
                    );
                  }
                  if (sd.type === 'order_tracking' && sd.payload) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 ml-9">
                        <OrderTrackingCard order={sd.payload as OrderTrackingResult} />
                      </div>
                    );
                  }
                  if (sd.type === 'campervan_results' && Array.isArray(sd.payload?.offers) && sd.payload.offers.length > 0) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 ml-9">
                        <CampervanResults data={sd.payload as CampervanResultsPayload} />
                      </div>
                    );
                  }
                  if (sd.type === 'trip_saved' && sd.payload?.trip_id) {
                    // Real save result from the server: the button opens the
                    // saved trip in the planner (link built from config server-side).
                    const href: string = sd.payload.links?.web_trip_url || sd.payload.links?.web_saved_url || '';
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 ml-9 bg-white border border-gray-200 rounded-xl p-3 text-sm">
                        <p className="font-semibold text-gray-800">{sd.payload.name}</p>
                        <p className="text-xs text-gray-500 mb-2">
                          Saved: {sd.payload.stop_count} stops, {sd.payload.total_nights} nights
                        </p>
                        {href && (
                          <a href={href} className="inline-block text-xs font-semibold" style={{ color: ACCENT }}>
                            Open trip
                          </a>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          })}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex gap-2 items-start">
              <Avatar size={28} icon={<MessageOutlined />} style={{ background: BRAND }} className="flex-shrink-0" />
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Spin size="small" />
                  <span>{statusText || 'Thinking...'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Login wall — shown when user tries a trip-planning action */}
          {loginRequired && (
            <div className="bg-white border border-[#e3f2fd] rounded-2xl p-5 shadow-sm text-center">
              <LoginOutlined className="text-3xl mb-2" style={{ color: ACCENT }} />
              <p className="text-sm font-semibold text-gray-800 mb-1">
                {loginAction === 'save_trip'
                  ? 'Sign in to save this trip'
                  : <>Sign in to check availability &amp; plan your trip</>}
              </p>
              <p className="text-xs text-gray-500 mb-4">
                {loginAction === 'save_trip'
                  ? 'Your trip is not saved yet. Sign in, then ask the assistant to save it again. Saved trips appear under My Trips on the Saved page and in the app.'
                  : 'Create a free account to unlock availability checks, booking holds, and trip planning features.'}
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleLogin('google')}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <GoogleOutlined />
                  Continue with Google
                </button>
                <button
                  onClick={() => handleLogin('facebook')}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-lg text-sm font-medium hover:bg-[#166FE5] transition-colors"
                >
                  <FacebookOutlined />
                  Continue with Facebook
                </button>
              </div>
              <button
                onClick={() => setLoginRequired(false)}
                className="mt-3 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Maybe later — keep browsing
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 bg-white border-t border-gray-200">
          <div className="flex gap-2">
            <Input
              ref={inputRef as any}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about campsites or gear..."
              disabled={isLoading}
              className="rounded-full"
              size="large"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => sendMessage()}
              loading={isLoading}
              disabled={!input.trim()}
              className="rounded-full border-none"
              style={{ background: '#10b981' }}
              size="large"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Campsite Card Component ──────────────────────────────────

function CampsiteCard({ site }: { site: CampsiteResult }) {
  // Generate slug from name for URL (numeric IDs cause 404s)
  const slug = site.name?.toLowerCase()
    .replace(/'/g, '').replace(/"/g, '').replace(/&/g, 'and')
    .replace(/[^a-z0-9 -]/g, '')
    .trim().replace(/\s+/g, '-').replace(/--+/g, '-') || String(site.id);
  // Ensure URL always includes Shopify app proxy prefix
  const detailUrl = (site.url && site.url.includes(withBase('/')))
    ? site.url
    : withBase(`/camp-sites/${slug}`);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <a
        href={detailUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex no-underline text-inherit"
      >
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 relative">
          {site.image_url ? (
            <img
              src={site.image_url}
              alt={site.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#e3f2fd]">
              <EnvironmentOutlined className="text-2xl text-accent/40" />
            </div>
          )}
          {site.is_premium && (
            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold text-amber-900 bg-amber-400/90">
              PREMIUM
            </span>
          )}
        </div>
        <div className="flex-1 p-2.5 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">
            {site.name}
          </h4>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
            <EnvironmentOutlined className="text-[10px]" />
            <span className="truncate">
              {[site.town, site.state].filter(Boolean).join(', ')}
              {site.distance_km ? ` (${site.distance_km}km)` : ''}
            </span>
          </div>
          {site.rating && !isNaN(Number(site.rating)) && Number(site.rating) > 0 && (
            <div className="flex items-center gap-1 mb-1">
              <StarFilled className="text-amber-500 text-[10px]" />
              <span className="text-xs font-medium">{Number(site.rating).toFixed(1)}</span>
            </div>
          )}
          {site.features && site.features.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {site.features.slice(0, 3).map(f => (
                <Tag
                  key={f}
                  className="text-[9px] px-1.5 py-0 border-0 bg-gray-100 text-gray-600 m-0"
                >
                  {f}
                </Tag>
              ))}
              {site.features.length > 3 && (
                <span className="text-[9px] text-gray-400">+{site.features.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </a>
      {/* Action bar — Book is the hero action when the site takes bookings */}
      <div className="px-2.5 py-2 border-t border-gray-100 flex items-center gap-2">
        {site.booking_url ? (
          <>
            <a
              href={site.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center text-xs font-bold no-underline px-3 py-1.5 rounded-lg text-white"
              style={{
                background: `linear-gradient(135deg, ${ACCENT} 0%, #1558b0 100%)`,
                boxShadow: '0 2px 6px rgba(0,102,204,0.3)',
              }}
            >
              Book now →
            </a>
            <a
              href={detailUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 text-xs font-semibold no-underline px-3 py-1.5 rounded-lg border"
              style={{ color: ACCENT, borderColor: `${ACCENT}55` }}
            >
              Details
            </a>
          </>
        ) : (
          <a
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center text-xs font-bold no-underline px-3 py-1.5 rounded-lg border"
            style={{ color: ACCENT, borderColor: `${ACCENT}55`, background: `${ACCENT}08` }}
          >
            View details →
          </a>
        )}
      </div>
    </div>
  );
}

// ── Product Card Component ───────────────────────────────────

function ProductCard({ product }: { product: ProductResult }) {
  return (
    <div>
      <ProductCardLink product={product} />
      {product.handle && (
        <PreviewAddToCart
          className="mt-1.5"
          compact
          handle={product.handle}
          title={product.title}
          image={product.image_url}
        />
      )}
    </div>
  );
}

function ProductCardLink({ product }: { product: ProductResult }) {
  const priceStr = product.price
    ? `$${product.price.toFixed(2)}`
    : '';
  const compareStr = product.compare_at_price && product.compare_at_price > (product.price || 0)
    ? `$${product.compare_at_price.toFixed(2)}`
    : '';

  return (
    <a
      href={product.product_url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow no-underline text-inherit"
    >
      <div className="flex">
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 relative">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#e3f2fd]">
              <ShoppingOutlined className="text-2xl text-[#1558b0]/40" />
            </div>
          )}
        </div>
        <div className="flex-1 p-2.5 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">
            {product.title}
          </h4>
          {product.vendor && (
            <div className="text-xs text-gray-500 mb-1 truncate">
              {product.vendor}
            </div>
          )}
          <div className="flex items-center gap-2 mb-1">
            {priceStr && (
              <span className="text-sm font-bold" style={{ color: ACCENT }}>
                {priceStr}
              </span>
            )}
            {compareStr && (
              <span className="text-xs text-gray-400 line-through">
                {compareStr}
              </span>
            )}
          </div>
          <div className="text-xs font-medium" style={{ color: ACCENT }}>
            View product →
          </div>
        </div>
      </div>
    </a>
  );
}

// ── Campervan Hire Cards (from search_campervans) ────────────
//
// The agent's DriveAway search renders here as a bookable rail:
// a slate summary strip (the "search receipt"), vehicle cards with
// photo/specs/price, and one dominant Book CTA per card. All links
// carry rel="sponsored" (affiliate: conversion=CAMPAUS).

const vanMoney = (n: number | null | undefined) =>
  n == null ? '' : `$${Math.round(n).toLocaleString('en-AU')}`;

function vanNights(data: CampervanResultsPayload): number | null {
  if (data.rental_days) return data.rental_days;
  const first = data.offers.find(o => o.rental_days);
  if (first?.rental_days) return first.rental_days;
  const from = data.query?.pickup_date;
  const to = data.query?.dropoff_date;
  if (from && to) {
    const ms = new Date(to).getTime() - new Date(from).getTime();
    if (!isNaN(ms) && ms > 0) return Math.max(1, Math.round(ms / 86400000));
  }
  return null;
}

function vanDateRange(from?: string, to?: string): string {
  if (!from || !to) return '';
  const f = new Date(from);
  const t = new Date(to);
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return '';
  const day = (d: Date) => d.getDate();
  const mon = (d: Date) => d.toLocaleDateString('en-AU', { month: 'short' });
  const sameMonth = f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear();
  if (sameMonth) return `${day(f)}–${day(t)} ${mon(t)} ${t.getFullYear()}`;
  return `${day(f)} ${mon(f)} – ${day(t)} ${mon(t)} ${t.getFullYear()}`;
}

function CampervanResults({ data }: { data: CampervanResultsPayload }) {
  const offers = data.offers.slice(0, 4); // server-sorted: available + cheapest first
  const nights = vanNights(data);
  const dates = vanDateRange(data.query?.pickup_date, data.query?.dropoff_date);
  const travellers = data.query?.passengers;
  const total = data.offer_count ?? data.offers.length;
  const bestIdx = data.offers.findIndex(o => o.available);

  return (
    <div className="space-y-2">
      {/* Search-receipt rail */}
      <div
        className="rounded-xl px-3.5 py-2.5 text-white flex items-center justify-between gap-3"
        style={{ background: `linear-gradient(120deg, ${BRAND} 0%, #1e293b 100%)` }}
      >
        <div className="min-w-0">
          <div className="text-[13px] font-semibold truncate">
            🚐 {data.pickup_city ? `Campervans from ${data.pickup_city}` : 'Campervan hire'}
          </div>
          <div className="text-[11px] text-white/70 flex items-center gap-1.5 flex-wrap">
            {dates && (
              <span className="inline-flex items-center gap-1">
                <CalendarOutlined className="text-[10px]" />{dates}
              </span>
            )}
            {nights ? <span>· {nights} night{nights === 1 ? '' : 's'}</span> : null}
            {travellers ? <span>· {travellers} traveller{travellers === 1 ? '' : 's'}</span> : null}
          </div>
        </div>
        {data.cheapest_aud != null && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] uppercase tracking-wide text-white/60">from</div>
            <div className="text-[15px] font-bold leading-tight">{vanMoney(data.cheapest_aud)}</div>
          </div>
        )}
      </div>

      {/* Vehicle cards */}
      {offers.map((o, i) => (
        <CampervanCard
          key={`${o.name}-${i}`}
          offer={o}
          nights={nights}
          bookingUrl={data.booking_url}
          isBestPrice={i === bestIdx}
        />
      ))}

      {/* View-all stays ON OUR SITE — /campervan-hire re-runs this exact
          search (deep-link params + auto-run). Booking attribution happens
          on the per-vehicle Book buttons; browsing stays with us. */}
      <div className="pt-0.5">
        <a
          href={withBase(
            `/campervan-hire?city=${encodeURIComponent(data.pickup_city || '')}` +
            `&from=${encodeURIComponent(data.query?.pickup_date || '')}` +
            `&to=${encodeURIComponent(data.query?.dropoff_date || '')}`
          )}
          target="_blank"
          rel="noopener"
          className="block text-center text-xs font-semibold no-underline rounded-lg py-2 border transition-colors"
          style={{ color: ACCENT, borderColor: `${ACCENT}55`, background: `${ACCENT}08` }}
        >
          Compare all {total} campervans &amp; filters →
        </a>
        <p className="text-[10px] text-gray-400 mt-1.5 mb-0 leading-snug">
          Live prices &amp; availability from DriveAway. Fees and inclusions confirmed at checkout.
        </p>
      </div>
    </div>
  );
}

function CampervanCard({
  offer,
  nights,
  bookingUrl,
  isBestPrice,
}: {
  offer: CampervanOffer;
  nights: number | null;
  bookingUrl?: string;
  isBestPrice?: boolean;
}) {
  const perNight =
    offer.price_aud != null && nights
      ? Math.round(offer.price_aud / Math.max(1, nights))
      : null;
  const specs = [
    offer.sleeps != null ? `Sleeps ${offer.sleeps}` : null,
    offer.seats != null ? `Seats ${offer.seats}` : null,
    offer.category || offer.style || null,
    offer.four_wd ? '4WD' : null,
  ].filter(Boolean) as string[];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Vehicle image — DriveAway supplies cutout renders, so contain them
          on a soft sky backdrop rather than cropping */}
      <div
        className="relative h-[132px]"
        style={{ background: 'linear-gradient(165deg, #f2f8ff 0%, #e4eef9 60%, #d9e7f4 100%)' }}
      >
        {offer.image ? (
          <img
            src={offer.image}
            alt={`${offer.brand || ''} ${offer.name || 'Campervan'}`.trim()}
            className="w-full h-full object-contain p-2.5"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CarOutlined className="text-4xl text-accent/30" />
          </div>
        )}
        {isBestPrice && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-bold text-amber-900 bg-amber-400/90">
            BEST PRICE
          </span>
        )}
        <span
          className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            offer.available
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-gray-200 text-gray-600'
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${offer.available ? 'bg-emerald-500' : 'bg-gray-400'}`}
          />
          {offer.available ? 'Available' : 'On request'}
        </span>
        {offer.brand_logo && (
          <span className="absolute bottom-2 left-2 bg-white/90 rounded-md px-1.5 py-1 shadow-sm">
            <img src={offer.brand_logo} alt={offer.brand || ''} className="h-3.5 w-auto block" loading="lazy" />
          </span>
        )}
      </div>

      <div className="p-3">
        {offer.brand && !offer.brand_logo && (
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{offer.brand}</div>
        )}
        <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1 truncate">
          {offer.name || 'Campervan'}
        </h4>
        {specs.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1.5">
            <TeamOutlined className="text-[10px] flex-shrink-0" />
            <span className="truncate">{specs.join(' · ')}</span>
          </div>
        )}
        {offer.tags && offer.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {offer.tags.slice(0, 4).map(t => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                {t}
              </span>
            ))}
            {offer.tags.length > 4 && (
              <span className="text-[9px] text-gray-400 py-0.5">+{offer.tags.length - 4}</span>
            )}
          </div>
        )}

        {/* Price + CTA row */}
        <div className="flex items-end justify-between gap-2 pt-1 border-t border-gray-100">
          <div className="pt-1.5">
            {offer.price_aud != null ? (
              <>
                <div className="text-[17px] font-extrabold leading-none text-gray-900">
                  {vanMoney(offer.price_aud)}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  total{nights ? ` · ${nights} nights` : ''}
                  {perNight ? ` · ${vanMoney(perNight)}/night` : ''}
                </div>
              </>
            ) : (
              <div className="text-[11px] text-gray-500">Price at checkout</div>
            )}
          </div>
          {/* Per-vehicle deep link (lands ON this van, dates prefilled);
              generic search URL only as fallback for older payloads. */}
          {(offer.booking_url || bookingUrl) && (
            <a
              href={offer.booking_url || bookingUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="flex-shrink-0 no-underline text-white text-xs font-bold px-4 py-2 rounded-lg mt-1.5"
              style={{
                background: `linear-gradient(135deg, ${ACCENT} 0%, #1558b0 100%)`,
                boxShadow: '0 2px 6px rgba(0,102,204,0.35)',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,102,204,0.45)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,102,204,0.35)';
              }}
            >
              Book now →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Save helper — reuses existing localStorage savedCampsites ──
function useSavedState(url: string) {
  const [isSaved, setIsSaved] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    setIsSaved(saved.some((c: any) => c.url === url));
  }, [url]);

  const toggle = (name: string, lat?: number, lng?: number, phone?: string, bookingUrl?: string) => {
    const saved = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    if (isSaved) {
      localStorage.setItem('savedCampsites', JSON.stringify(saved.filter((c: any) => c.url !== url)));
      setIsSaved(false);
    } else {
      saved.push({
        name,
        url,
        savedAt: new Date().toISOString(),
        latitude: lat ? String(lat) : undefined,
        longitude: lng ? String(lng) : undefined,
        phone: phone || undefined,
        booking_url: bookingUrl || undefined,
      });
      localStorage.setItem('savedCampsites', JSON.stringify(saved));
      setIsSaved(true);
    }
  };

  return { isSaved, toggle };
}

// ── Campsite Detail Card (from get_campsite_details) ──────────

function CampsiteDetailCard({ detail }: { detail: CampsiteDetail }) {
  const slug = detail.name?.toLowerCase()
    .replace(/'/g, '').replace(/"/g, '').replace(/&/g, 'and')
    .replace(/[^a-z0-9 -]/g, '')
    .trim().replace(/\s+/g, '-').replace(/--+/g, '-') || '';
  const slugUrl = `${SITE_BRAND.siteUrl}${withBase(`/camp-sites/${slug}`)}`;
  // Ensure the URL always includes the Shopify app proxy prefix
  const url = (detail.url && detail.url.includes(withBase('/')))
    ? detail.url
    : slugUrl;
  const { isSaved, toggle } = useSavedState(url);
  const town = detail.address?.town || '';
  const state = detail.address?.state || '';
  const location = [town, state].filter(Boolean).join(', ');

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex no-underline text-inherit"
      >
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 relative">
          {detail.image_url ? (
            <img src={detail.image_url} alt={detail.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#e3f2fd]">
              <EnvironmentOutlined className="text-2xl text-accent/40" />
            </div>
          )}
          {detail.is_premium && (
            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold text-amber-900 bg-amber-400/90">
              PREMIUM
            </span>
          )}
        </div>
        <div className="flex-1 p-2.5 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">{detail.name}</h4>
          </div>
          {location && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <EnvironmentOutlined className="text-[10px]" />
              <span className="truncate">{location}</span>
            </div>
          )}
          {detail.rating && !isNaN(Number(detail.rating)) && Number(detail.rating) > 0 && (
            <div className="flex items-center gap-1 mb-1">
              <StarFilled className="text-amber-500 text-[10px]" />
              <span className="text-xs font-medium">{Number(detail.rating).toFixed(1)}</span>
            </div>
          )}
          {detail.features && detail.features.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {detail.features.slice(0, 3).map(f => (
                <Tag key={f} className="text-[9px] px-1.5 py-0 border-0 bg-gray-100 text-gray-600 m-0">{f}</Tag>
              ))}
              {detail.features.length > 3 && (
                <span className="text-[9px] text-gray-400">+{detail.features.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </a>
      {/* Action bar — Book is the hero action when the site takes bookings */}
      <div className="px-2.5 py-2 border-t border-gray-100 flex items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggle(detail.name, detail.address?.latitude, detail.address?.longitude, detail.address?.phone, detail.booking_url);
          }}
          className="flex-shrink-0 flex items-center gap-1 text-xs transition-colors px-1"
          style={{ color: isSaved ? '#ef4444' : '#9ca3af' }}
        >
          {isSaved ? <HeartFilled className="text-red-500" /> : <HeartOutlined />}
          {isSaved ? 'Saved' : 'Save'}
        </button>
        {detail.booking_url ? (
          <>
            <a
              href={detail.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center text-xs font-bold no-underline px-3 py-1.5 rounded-lg text-white"
              style={{
                background: `linear-gradient(135deg, ${ACCENT} 0%, #1558b0 100%)`,
                boxShadow: '0 2px 6px rgba(0,102,204,0.3)',
              }}
            >
              Book now →
            </a>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 text-xs font-semibold no-underline px-3 py-1.5 rounded-lg border"
              style={{ color: ACCENT, borderColor: `${ACCENT}55` }}
            >
              Details
            </a>
          </>
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center text-xs font-bold no-underline px-3 py-1.5 rounded-lg border"
            style={{ color: ACCENT, borderColor: `${ACCENT}55`, background: `${ACCENT}08` }}
          >
            View details →
          </a>
        )}
      </div>
    </div>
  );
}

function OrderTrackingCard({ order }: { order: OrderTrackingResult }) {
  if (!order.found) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <p className="text-sm text-gray-600 mb-2">{order.message}</p>
        {order.account_url && (
          <a
            href={order.account_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm font-medium"
            style={{ color: ACCENT }}
          >
            View your account →
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header — order number + status */}
      <div className="px-4 py-3 border-b border-gray-100" style={{ background: `${ACCENT}08` }}>
        <div className="flex justify-between items-center">
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Order</div>
            <div className="text-sm font-bold text-gray-900">{order.order_number}</div>
          </div>
          <div className="text-right">
            {order.order_date && (
              <div className="text-[10px] text-gray-500">{order.order_date}</div>
            )}
            <div className="text-sm font-semibold" style={{ color: ACCENT }}>{order.status}</div>
          </div>
        </div>
      </div>

      {/* Items list */}
      {order.items && order.items.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-100">
          {order.items.map((item, i) => (
            <div key={i} className="flex justify-between text-xs text-gray-700 py-0.5">
              <span className="truncate mr-2">{item.title}</span>
              <span className="text-gray-400 flex-shrink-0">×{item.quantity}</span>
            </div>
          ))}
        </div>
      )}

      {/* Shipping + tracking */}
      <div className="px-4 py-2.5">
        {order.shipping_location && (
          <div className="text-xs text-gray-500 mb-1">
            <EnvironmentOutlined className="mr-1" />
            Shipping to: <span className="text-gray-700 font-medium">{order.shipping_location}</span>
          </div>
        )}
        {order.tracking && order.tracking.map((t, i) => (
          <div key={i} className="text-xs mt-1">
            {t.carrier && <span className="text-gray-500">{t.carrier}: </span>}
            {t.tracking_url ? (
              <a
                href={t.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
                style={{ color: ACCENT }}
              >
                {t.tracking_number || 'Track shipment'}
              </a>
            ) : t.tracking_number ? (
              <span className="text-gray-700 font-medium">{t.tracking_number}</span>
            ) : null}
          </div>
        ))}
      </div>

      {/* Action links */}
      <div className="px-4 py-2.5 border-t border-gray-100 flex gap-4">
        {order.tracking && order.tracking.length > 0 && order.tracking[0].tracking_url && (
          <a
            href={order.tracking[0].tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium"
            style={{ color: ACCENT }}
          >
            Track shipment →
          </a>
        )}
        {order.account_url && (
          <a
            href={order.account_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium"
            style={{ color: ACCENT }}
          >
            View in your account →
          </a>
        )}
      </div>
    </div>
  );
}
